import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Trash2, Search, ShieldCheck, X } from "lucide-react";
import { Navigate } from "react-router-dom";

export default function AdminUsers() {
  const { user, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/users").then(r => setUsers(r.data)).catch(e => setMsg(formatError(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div className="text-slate-500">Laden...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace/>;

  const verify = async (id) => {
    try { await api.post(`/admin/users/${id}/verify`); load(); setMsg("✓ Gebruiker geverifieerd"); setTimeout(() => setMsg(""), 2500); }
    catch (e) { setMsg(formatError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Gebruiker en al z'n sprongen permanent verwijderen?")) return;
    try { await api.delete(`/admin/users/${id}`); load(); }
    catch (e) { setMsg(formatError(e)); }
  };

  const filtered = users.filter(u => {
    if (!q) return true;
    return (u.email + " " + u.name).toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div data-testid="admin-users-page">
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Admin</div>
      <h1 className="heading-display text-4xl mt-1 mb-2">Gebruikersbeheer</h1>
      <p className="text-slate-600 mb-6">Activeer accounts handmatig als de verificatiemail niet aankomt.</p>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek op e-mail of naam..." className="w-full pl-9 pr-3 py-2.5 border rounded-lg bg-white" data-testid="user-search"/>
        </div>
        <div className="text-sm text-slate-500">{filtered.length} / {users.length} gebruikers</div>
      </div>

      {msg && <div className="mb-4 p-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded text-sm" data-testid="admin-msg">{msg}</div>}

      <div className="gs-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Naam</th>
              <th className="px-4 py-3 font-semibold">E-mail</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Rol</th>
              <th className="px-4 py-3 font-semibold">Geregistreerd</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  {u.email_verified ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 size={12}/> Bevestigd</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><X size={12}/> Onbevestigd</span>
                  )}
                </td>
                <td className="px-4 py-3">{u.role === "admin" ? <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500 text-white">admin</span> : <span className="text-xs text-slate-500">user</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!u.email_verified && (
                    <button onClick={() => verify(u.id)} className="text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-semibold mr-2 inline-flex items-center gap-1" data-testid={`verify-${u.id}`}>
                      <ShieldCheck size={14}/> Activeer
                    </button>
                  )}
                  {u.role !== "admin" && (
                    <button onClick={() => del(u.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-${u.id}`}><Trash2 size={16}/></button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">Geen gebruikers gevonden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
